import React from "react";
import { bubbleTime, formatBytes, fullTime, mediaUrl } from "./waUtils";

const STATUS_ICON = {
  queued: { icon: "fa-regular fa-clock", label: "Queued" },
  sending: { icon: "fa-regular fa-clock", label: "Sending" },
  sent: { icon: "fa-solid fa-check", label: "Sent" },
  delivered: { icon: "fa-solid fa-check-double", label: "Delivered" },
  read: { icon: "fa-solid fa-check-double wa-tick--read", label: "Read" },
  failed: { icon: "fa-solid fa-circle-exclamation wa-tick--failed", label: "Failed" },
};

const DOC_ICON = (mime = "") => {
  if (mime.includes("pdf")) return "fa-regular fa-file-pdf";
  if (mime.includes("sheet") || mime.includes("excel")) return "fa-regular fa-file-excel";
  if (mime.includes("word")) return "fa-regular fa-file-word";
  if (mime.includes("presentation") || mime.includes("powerpoint")) return "fa-regular fa-file-powerpoint";
  return "fa-regular fa-file-lines";
};

export const StatusTick = ({ message }) => {
  const s = STATUS_ICON[message.status];
  if (!s || message.direction !== "outbound") return null;
  const title = message.status === "failed"
    ? `Failed: ${message.error?.message || "Unknown error"}${message.error?.code ? ` (code ${message.error.code})` : ""}`
    : message.status === "queued" && message.error
      ? `Retrying — ${message.error.message}`
      : s.label;
  return <i className={`wa-tick ${s.icon}`} title={title} aria-label={title} />;
};

const MediaBlock = ({ message, onImageClick, onMediaError }) => {
  const m = message.media;
  if (!m) return null;
  const url = mediaUrl(m.url);
  if (!url) {
    return (
      <div className="wa-file">
        <i className={`wa-file__icon ${m.pending ? "fa-solid fa-spinner wa-spin" : "fa-solid fa-triangle-exclamation"}`} />
        <div>
          <div className="wa-file__name">{m.filename || message.type}</div>
          <div className="wa-file__sub">{m.pending ? "Downloading from WhatsApp…" : "Media unavailable — it may have expired before it could be saved."}</div>
        </div>
      </div>
    );
  }
  switch (message.type) {
    case "image":
      return <img className="wa-bubble__img" src={url} alt={message.body || "Image"} loading="lazy" onError={onMediaError} onClick={() => onImageClick?.(url, message)} />;
    case "sticker":
      return <img src={url} alt="Sticker" style={{ width: 120, height: 120, objectFit: "contain" }} loading="lazy" onError={onMediaError} />;
    case "video":
      return <video src={url} controls preload="metadata" onError={onMediaError} style={{ maxWidth: "100%", maxHeight: 320, borderRadius: 8, display: "block", marginBottom: 4 }} />;
    case "audio":
      return <audio src={url} controls preload="none" onError={onMediaError} style={{ width: 260, display: "block", marginBottom: 4 }} />;
    default:
      return (
        <a className="wa-file" href={`${url}&download=1`} target="_blank" rel="noopener noreferrer" style={{ textDecoration: "none", color: "inherit" }}>
          <i className={`wa-file__icon ${DOC_ICON(m.mimeType)}`} />
          <div style={{ minWidth: 0 }}>
            <div className="wa-file__name" title={m.filename}>{m.filename || "Document"}</div>
            <div className="wa-file__sub">{[formatBytes(m.size), (m.mimeType || "").split("/")[1]].filter(Boolean).join(" · ")} · Download</div>
          </div>
        </a>
      );
  }
};

const LocationBlock = ({ location }) => {
  if (!location) return null;
  const href = `https://www.google.com/maps?q=${encodeURIComponent(`${location.latitude},${location.longitude}`)}`;
  return (
    <a className="wa-file" href={href} target="_blank" rel="noopener noreferrer" style={{ textDecoration: "none", color: "inherit" }}>
      <i className="wa-file__icon fa-solid fa-location-dot" />
      <div>
        <div className="wa-file__name">{location.name || "Shared location"}</div>
        <div className="wa-file__sub">{location.address || `${location.latitude}, ${location.longitude}`}</div>
      </div>
    </a>
  );
};

const ContactsBlock = ({ contacts }) =>
  (contacts || []).map((c, i) => (
    <div className="wa-file" key={i}>
      <i className="wa-file__icon fa-regular fa-address-card" />
      <div>
        <div className="wa-file__name">{c.name?.formatted_name || "Contact"}</div>
        <div className="wa-file__sub">{(c.phones || []).map((p) => p.phone).filter(Boolean).join(", ") || "No phone"}</div>
      </div>
    </div>
  ));

const MessageBubble = ({ message, quoted, onImageClick, onRetry, canRetry, onMediaError }) => {
  if (message.type === "note") {
    return (
      <div className="wa-msg wa-msg--note">
        <div className="wa-note">
          <div className="wa-note__head"><i className="fa-solid fa-note-sticky" /> Internal note · {message.sentByName || "Team"} · {bubbleTime(message.timestamp)}</div>
          <div className="wa-bubble__text">{message.body}</div>
        </div>
      </div>
    );
  }

  const out = message.direction === "outbound";
  const failed = message.status === "failed";
  const isMedia = ["image", "video", "audio", "document", "sticker"].includes(message.type);

  return (
    <div className={`wa-msg ${out ? "wa-msg--out" : "wa-msg--in"} ${failed ? "wa-msg--failed" : ""}`} data-wamid={message.wamid || undefined}>
      <div className="wa-bubble">
        {out && message.sentByName && <div className="wa-bubble__sender">{message.sentByName}</div>}
        {message.type === "template" && (
          <div className="wa-bubble__tpl"><i className="fa-solid fa-file-lines" /> Template · {message.templateName}</div>
        )}
        {quoted && (
          <div style={{ borderLeft: "3px solid var(--wa-accent)", padding: "4px 8px", marginBottom: 6, background: "rgba(15,23,42,.04)", borderRadius: 4, fontSize: 12, color: "var(--wa-muted)" }}>
            <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 360 }}>{quoted.body || quoted.type}</div>
          </div>
        )}
        {isMedia && <MediaBlock message={message} onImageClick={onImageClick} onMediaError={onMediaError} />}
        {message.type === "location" && <LocationBlock location={message.location} />}
        {message.type === "contacts" && <ContactsBlock contacts={message.contacts} />}
        {message.type === "reaction" ? (
          <div className="wa-reaction">{message.body}</div>
        ) : message.type === "unsupported" ? (
          <div className="wa-reaction"><i className="fa-solid fa-circle-info" /> {message.body || "This message type isn't supported by WhatsApp Cloud API."}</div>
        ) : (
          message.body && message.type !== "location" && message.type !== "contacts" && <div className="wa-bubble__text">{message.body}</div>
        )}
        <div className="wa-bubble__meta" title={fullTime(message.timestamp)}>
          {bubbleTime(message.timestamp)}
          <StatusTick message={message} />
        </div>
        {failed && (
          <div className="wa-bubble__error" role="alert">
            <i className="fa-solid fa-circle-exclamation" style={{ marginTop: 2 }} />
            <div style={{ flex: "1 1 auto" }}>
              <b>Not delivered.</b> {message.error?.message || "WhatsApp rejected the message."}
              {message.error?.code ? ` (code ${message.error.code})` : ""}
            </div>
            {canRetry && (
              <button type="button" className="wa-btn wa-btn--sm" onClick={() => onRetry?.(message)}>Retry</button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default MessageBubble;
